/**
 * liveSiteEvent.ts
 * ----------------
 * Reducers related to live site events used across all portals.
 */

import { LOCATION_CHANGE } from 'connected-react-router';
import { Record } from 'immutable';

import { Action, AsyncAction, PayloadAction } from '../../actions/types';
import {
    PreloadLiveSiteEvent, Shared_ClearLiveSiteEventViewContext, Shared_ReceiveGetLiveSiteEvent,
    Shared_RequestGetLiveSiteEvent, Shared_SetLiveSiteEventViewContext,
} from '../../constants/actions';
import { Status } from '../../constants/statuses';
import { GetLiveSiteEventResponse } from '../../models/dto/UpdatePortal';
import { PreloadLiveSiteEventPayload } from '../../models/payloads/preload';
import { asyncRequestReducer } from '../shared/common';
import { difference } from '../../utilities/time';

interface LiveSiteEventStateFields {
    getLiveSiteEventError: string;
    getLiveSiteEventStatus: Status;
    lastRequestTimestamp: Date;
    liveSiteEvent: GetLiveSiteEventResponse | undefined;
    liveSiteEventViewId: number | undefined;
}

/**
 * Application substate
 */
export class LiveSiteEventState extends Record<LiveSiteEventStateFields>({
    getLiveSiteEventError: '',
    getLiveSiteEventStatus: 'none',
    lastRequestTimestamp: new Date(),
    liveSiteEvent: undefined,
    liveSiteEventViewId: undefined,
}) { }

export const initialState = new LiveSiteEventState();

/**
 * Private variables
 */

const fiveMinutesInMilliseconds = 5 * 60 * 1000;

function clearLiveSiteEventViewContextReducer (state: LiveSiteEventState) {
    let nextState = state
        .set('getLiveSiteEventError', initialState.getLiveSiteEventError)
        .set('getLiveSiteEventStatus', initialState.getLiveSiteEventStatus);

    if (state.liveSiteEvent !== initialState.liveSiteEvent) {
        nextState = nextState.set('liveSiteEvent', initialState.liveSiteEvent);
    }

    if (state.liveSiteEventViewId !== initialState.liveSiteEventViewId) {
        nextState = nextState.set('liveSiteEventViewId', initialState.liveSiteEventViewId);
    }

    return nextState;
}

function locationChangeReducer (state: LiveSiteEventState) {
    // Note: for now, we listen to location changes on any route to check staleness. We can receive and check the action
    // data in the future if we only want to update on certain location changes.
    const { lastRequestTimestamp } = state;

    let nextState = state;
    const now = new Date();
    const requestTimestampDiff = difference(now, lastRequestTimestamp);

    if (requestTimestampDiff > fiveMinutesInMilliseconds) {
        nextState = nextState
            .set('getLiveSiteEventError', initialState.getLiveSiteEventError)
            .set('getLiveSiteEventStatus', initialState.getLiveSiteEventStatus);
    }

    return nextState;
}

function preloadLiveSiteEventReducer (state: LiveSiteEventState, action: PayloadAction<PreloadLiveSiteEventPayload>) {
    const { payload } = action;

    return state.set('getLiveSiteEventError', initialState.getLiveSiteEventError)
        .set('getLiveSiteEventStatus', 'succeeded')
        .set('liveSiteEvent', payload)
        .set('liveSiteEventViewId', payload !== undefined ? payload.id : undefined);
}

function receiveGetLiveSiteEventReducer (state: LiveSiteEventState, action: PayloadAction<GetLiveSiteEventResponse>) {
    const { payload } = action;

    return state.set('liveSiteEvent', payload);
}

const requestGetLiveSiteEventReducer = asyncRequestReducer<LiveSiteEventState>(
    (state, error) => state.set('getLiveSiteEventError', error),
    (state, status) => state.set('getLiveSiteEventStatus', status)
);

function setLiveSiteEventViewContextReducer (state: LiveSiteEventState, action: PayloadAction<number>) {
    const { payload: id } = action;

    let nextState = state;

    if (id !== state.liveSiteEventViewId) {
        nextState = nextState
            .set('getLiveSiteEventError', initialState.getLiveSiteEventError)
            .set('getLiveSiteEventStatus', initialState.getLiveSiteEventStatus)
            .set('liveSiteEventViewId', id);
    }

    return nextState;
}

/**
 * Reducer that updates the application state in response to live site event-related actions.
 * @param state Current application state
 * @param action Action to be performed on state
 * @returns Updated application state
 */
export function liveSiteEventReducer (state: LiveSiteEventState = initialState, action?: Action) {
    if (action === undefined) {
        return state;
    }

    switch (action.type) {
        case LOCATION_CHANGE:
            return locationChangeReducer(state);

        case PreloadLiveSiteEvent:
            return preloadLiveSiteEventReducer(state, action as PayloadAction<PreloadLiveSiteEventPayload>);

        case Shared_ClearLiveSiteEventViewContext:
            return clearLiveSiteEventViewContextReducer(state);

        case Shared_ReceiveGetLiveSiteEvent:
            return receiveGetLiveSiteEventReducer(state, action as PayloadAction<GetLiveSiteEventResponse>);

        case Shared_RequestGetLiveSiteEvent:
            return requestGetLiveSiteEventReducer(state, action as AsyncAction);

        case Shared_SetLiveSiteEventViewContext:
            return setLiveSiteEventViewContextReducer(state, action as PayloadAction<number>);

        default:
            return state;
    }
}
